import { FC, memo, ReactElement, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NavigateFunction, useNavigate } from 'react-router';
import i18next from 'i18next';
import { Card, Col, Row } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import { isEmailVerificationPending, useExtendedAuth } from '../util/auth0';
import { emitErrorNotification } from './Notification';
import PatientRoomCodeBox from './loginSignup/PatientRoomCodeBox';
import { LoginBoxAuth0 } from './loginSignup/LoginBoxAuth0';

const LoginPage: FC = (): ReactElement => {
  const { isAuthenticated, logout, user } = useAuth0();
  const { isApprovedTherapist } = useExtendedAuth();
  const navigate: NavigateFunction = useNavigate();

  const isApproved: () => Promise<void> = useCallback(async (): Promise<void> => {
    try {
      const isApproved: boolean = await isApprovedTherapist();
      navigate(isApproved ? '/patients' : '/account-setup');
    } catch {
      emitErrorNotification(i18next.t('errorMessageTemplate.genericMsg'));
      await logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [isApprovedTherapist, navigate, logout]);

  useEffect(() => {
    if (isAuthenticated && user && !isEmailVerificationPending(user)) {
      void isApproved();
    }
  }, [isApproved, user, isAuthenticated]);

  return (
    <div className="py-5">
      <h1
        className="text-center text-white fw-bold mb-3"
        style={{
          fontSize: 'clamp(2.8rem, 2.4vw, 3.2rem)',
          letterSpacing: '0.2px',
        }}
      >
        {i18next.t('loginPage.welcome')}
      </h1>

      <Row className="g-4 justify-content-center">
        <Col xs={12} md={6} lg={5} className="d-flex justify-content-center">
          <Card
            className="shadow-sm border rounded-2 overflow-hidden bg-white w-100"
            style={{ maxWidth: '40rem' }}
          >
            <Card.Header
              className="d-flex align-items-center justify-content-center fw-bold text-white"
              style={{
                height: '7rem',
                padding: '0.6rem 0.8rem',
                fontSize: '2.6rem',
                backgroundColor: '#91bfb7'
              }}
            >
              {i18next.t('loginPage.patients')}
            </Card.Header>
            <Card.Body
              className="p-3"
              style={{ backgroundColor: '#d4e7e4' }}
            >
              <PatientRoomCodeBox />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={6} lg={5} className="d-flex justify-content-center">
          <Card
            className="shadow-sm border rounded-2 overflow-hidden bg-white w-100"
            style={{ maxWidth: '40rem' }}
          >
            <Card.Header
              className="d-flex align-items-center justify-conent-center fw-bold text-white"
              style={{
                height: '7rem',
                padding: '0.6rem 0.8rem',
                fontSize: '2.6rem',
                backgroundColor: '#002b4b'
              }}
            >
              {i18next.t('loginPage.therapists')}
            </Card.Header>
            <Card.Body
              className="p-3"
              style={{ backgroundColor: '#d4e1ea' }}
            >
              <p
                className="mb-3"
                style={{
                  fontSize: '1.4rem',
                  lineHeight: 1.45,
                  color: '#161616'
                }}
              >
                <Trans
                  i18nKey="loginPage.therapistsSubHeading"
                  components={[
                    <a
                      key="link"
                      href="https://www.enlivio.com/intendeduse/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-decoration-underline fw-semibold"
                    />,
                  ]}
                />
              </p>

              <LoginBoxAuth0 login />
            </Card.Body>

          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default memo(LoginPage);
